import {
  Dialog,
  type DialogBackdropProps,
  type DialogCloseProps,
  type DialogDescriptionProps,
  type DialogPopupProps,
  type DialogRootProps,
  type DialogTitleProps,
  type DialogTriggerProps,
  type DialogViewportProps,
  mergeProps,
  ScrollArea,
  type ScrollAreaContentProps,
  type ScrollAreaRootProps,
  type ScrollAreaScrollbarProps,
  type ScrollAreaThumbProps,
  type ScrollAreaViewportProps,
} from '@base-ui/react';
import type { PropsWithChildren, ReactNode } from 'react';
import { IoClose } from 'react-icons/io5';
import { FuuzeButton, type FuuzeButtonProps } from './fuuze-button.js';
import styles from './fuuze-dialog.module.css';

function Backdrop(props: DialogBackdropProps) {
  const mergedProps = mergeProps<typeof Dialog.Backdrop>(props, { className: styles.Backdrop });
  return <Dialog.Backdrop {...mergedProps} />;
}

function Popup(props: DialogPopupProps) {
  const mergedProps = mergeProps<typeof Dialog.Popup>(props, { className: styles.Popup });
  return <Dialog.Popup {...mergedProps} />;
}

function Viewport(props: DialogViewportProps) {
  const mergedProps = mergeProps<typeof Dialog.Viewport>(props, { className: styles.Viewport });
  return <Dialog.Viewport {...mergedProps} />;
}

function Title(props: DialogTitleProps) {
  const mergedProps = mergeProps<typeof Dialog.Title>(props, { className: styles.Title });
  return <Dialog.Title {...mergedProps} />;
}

function Description(props: DialogDescriptionProps) {
  const mergedProps = mergeProps<typeof Dialog.Description>(props, { className: styles.Description });
  return <Dialog.Description {...mergedProps} />;
}

function ScrollAreaRoot(props: ScrollAreaRootProps) {
  const mergedProps = mergeProps<typeof ScrollArea.Root>(props, { className: styles.ScrollAreaRoot });
  return <ScrollArea.Root {...mergedProps} />;
}

function ScrollAreaViewport(props: ScrollAreaViewportProps) {
  const mergedProps = mergeProps<typeof ScrollArea.Viewport>(props, { className: styles.ScrollAreaViewport });
  return <ScrollArea.Viewport {...mergedProps} />;
}

function ScrollAreaContent(props: ScrollAreaContentProps) {
  const mergedProps = mergeProps<typeof ScrollArea.Content>(props, { className: styles.ScrollAreaContent });
  return <ScrollArea.Content {...mergedProps} />;
}

function Scrollbar(props: ScrollAreaScrollbarProps) {
  const mergedProps = mergeProps<typeof ScrollArea.Scrollbar>(props, { className: styles.Scrollbar });
  return <ScrollArea.Scrollbar {...mergedProps} />;
}

function ScrollbarThumb(props: ScrollAreaThumbProps) {
  const mergedProps = mergeProps<typeof ScrollArea.Thumb>(props, { className: styles.ScrollbarThumb });
  return <ScrollArea.Thumb {...mergedProps} />;
}

function Close(props: DialogCloseProps) {
  const mergedProps = mergeProps<typeof Dialog.Close>(props, { className: styles.Close });
  return (
    <Dialog.Close
      render={(props, state) => (
        <FuuzeButton {...props} icon style={{ ...props.style }}>
          <IoClose size={32} />
        </FuuzeButton>
      )}
      {...mergedProps}
    ></Dialog.Close>
  );
}

export const FuuzeDialog = {
  Backdrop,
  Close,
  Description,
  Popup,
  Portal: Dialog.Portal,
  Root: Dialog.Root,
  Title,
  Trigger: Dialog.Trigger,
  Viewport,
  ScrollAreaRoot,
  ScrollAreaViewport,
  ScrollAreaContent,
  Scrollbar,
  ScrollbarThumb,
};

type FuuzeDialogComponentProps = PropsWithChildren<{
  RootProps?: DialogRootProps;
  TriggerProps?: DialogTriggerProps;
  title?: string;
  description?: string | ReactNode;
  PopupProps?: DialogPopupProps;
  showCloseButton?: boolean;
  scrollable?: boolean;
  BackdropProps?: DialogBackdropProps;
  ScrollAreaContentProps?: ScrollAreaContentProps;
}>;

export function FuuzeDialogComponent(props: FuuzeDialogComponentProps) {
  return (
    <FuuzeDialog.Root {...props.RootProps}>
      {props.TriggerProps && <FuuzeDialog.Trigger {...props.TriggerProps} />}

      <FuuzeDialog.Portal>
        <FuuzeDialog.Backdrop {...props.BackdropProps} />

        <FuuzeDialog.Viewport>
          <FuuzeDialog.Popup {...props.PopupProps}>
            {(props.title || props.showCloseButton) && (
              <div className={styles.PopupHeader}>
                {props.title && <FuuzeDialog.Title>{props.title}</FuuzeDialog.Title>}
                {props.showCloseButton && <FuuzeDialog.Close />}
              </div>
            )}

            {props.description && typeof props.description === 'string' ? (
              <FuuzeDialog.Description>{props.description}</FuuzeDialog.Description>
            ) : (
              props.description
            )}

            {props.scrollable ? (
              <FuuzeDialog.ScrollAreaRoot>
                <FuuzeDialog.ScrollAreaViewport>
                  <FuuzeDialog.ScrollAreaContent {...props.ScrollAreaContentProps}>
                    {props.children}
                  </FuuzeDialog.ScrollAreaContent>
                </FuuzeDialog.ScrollAreaViewport>

                <FuuzeDialog.Scrollbar>
                  <FuuzeDialog.ScrollbarThumb />
                </FuuzeDialog.Scrollbar>
              </FuuzeDialog.ScrollAreaRoot>
            ) : (
              <div className={styles.ScrollAreaContent}>{props.children}</div>
            )}
          </FuuzeDialog.Popup>
        </FuuzeDialog.Viewport>
      </FuuzeDialog.Portal>
    </FuuzeDialog.Root>
  );
}
